import React from "react";
import { ArrowRight, Waves, Wind, UtensilsCrossed, Sun, MessageCircle } from "lucide-react";
import useReveal from "../hooks/useReveal";
import WindPalm from "../components/WindPalm";
import AnimatedText from "../components/AnimatedText";
import SEO from "../components/SEO";
import { SITE, POOL_IMAGE, VILLA_EXTERIOR, EXPERIENCE_BLOCKS, PALMS } from "../mock";

const icons = [Waves, Wind, UtensilsCrossed, Sun];

const Experience = () => {
  const ref = useReveal();
  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Experience — Surf, sea, and the slower hour"
        description="Less than a kilometer from San Juan del Sur Beach. Surf at Maderas, Playa Hermosa and Remanso; yoga, in-room breakfast, and sunset boat trips around the bay."
        path="/experience"
      />
      {/* Hero */}
      <section className="relative pt-40 pb-12 md:pt-48 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
          Experience
        </div>
        <h1 className="reveal font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-[#1f2a24] max-w-5xl">
          <span className="block"><AnimatedText text="The slow side" stagger={30} baseDelay={150} /></span>
          <span className="block opacity-80" style={{ fontStyle: "italic", fontWeight: 500 }}>
            <AnimatedText text="of the Pacific coast." stagger={28} baseDelay={550} />
          </span>
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="overflow-hidden">
          <img
            src={POOL_IMAGE}
            alt="Pool overlooking the ocean"
            className="reveal-img w-full h-[88svh] md:h-[80vh] object-cover"
          />
        </div>
      </section>

      {/* Beach */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
            The Beach
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f2a24] leading-[1.05]">
            San Juan del Sur, on foot.
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="reveal text-[#3a4a40] text-base md:text-[17px] leading-[1.85]">
            The bay is less than a kilometer from the door — a wide crescent
            of soft sand held between two headlands, where wooden fishing boats
            rock at anchor and the sunset draws the whole town to the water.
            Walk for breakfast, swim before lunch, return for an afternoon by
            the pool.
          </p>
        </div>
      </section>

      {/* Experience cards */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-2 gap-px bg-[#d8cfbd]">
          {EXPERIENCE_BLOCKS.map((b, i) => {
            const Icon = icons[i] || Sun;
            return (
              <div
                key={b.title}
                className="reveal bg-[#f5f0e6] p-10 md:p-14 hover:bg-[#ebe3d2] transition-colors duration-700"
              >
                <Icon className="text-[#2f4438] mb-6" size={22} />
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#1f2a24] mb-4">
                  {b.title}
                </h3>
                <p className="text-[#3a4a40] text-[15px] md:text-base leading-[1.85]">
                  {b.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Image break */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="overflow-hidden img-hover">
          <img
            src={VILLA_EXTERIOR}
            alt="Casa Elio at dusk"
            className="reveal-img w-full h-[88svh] md:h-[80vh] object-cover"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#ebe3d2] overflow-hidden">
        <WindPalm
          src={PALMS.pair}
          side="right"
          offsetX={-110}
          bottom={-50}
          width={420}
          opacity={0.55}
          parallax={0.08}
          scrollIntensity={1.2}
          swayDuration={9.0}
          swayPhase={-3}
          hideOnMobile
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-5">
            Stay With Us
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-6xl text-[#1f2a24] leading-[1.05]">
            Step into the slower coast.
          </h2>
          <div className="reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={SITE.airbnbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#1f2a24]/30 text-[#1f2a24] hover:bg-[#1f2a24] hover:text-[#f5f0e6] transition-colors"
            >
              Airbnb
              <ArrowRight size={14} />
            </a>
            <a
              href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                "Hi Casa Elio! I'd like to book a stay in San Juan del Sur."
              )}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.3em] px-7 py-4 bg-[#2f4438] text-[#f5f0e6] hover:bg-[#1f2a24] transition-colors"
            >
              <MessageCircle size={14} />
              Book Now
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#1f2a24]/30 text-[#1f2a24] hover:bg-[#1f2a24] hover:text-[#f5f0e6] transition-colors"
            >
              Booking.com
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Experience;
